import { reaction, toJS } from "mobx";
import { createContext, useContext, useEffect, useState } from "react";
import type { PropsWithChildren } from "react";
import { assert } from "utils";

import { updateIsomorphicRequestData } from "src/lib/isomorphicRequestData/isomorphicRequestDataRef";

import { type MaybeAuthAuthenticate, AuthStore } from "./AuthStore";

interface AuthContextValue {
  store: AuthStore;
}

const AuthContext = createContext<AuthContextValue | null>(null);

type AuthContextProviderProps = PropsWithChildren & {
  authenticatedData: MaybeAuthAuthenticate;
};

export function AuthContextProvider(props: AuthContextProviderProps) {
  const [value] = useState(() => {
    return { store: new AuthStore(props.authenticatedData) };
  });

  useEffect(() => {
    reaction(
      () => toJS(value.store._authenticatedData),
      (authData) => {
        updateIsomorphicRequestData({ session: authData });
      },
    );
  }, []);

  return (
    <AuthContext.Provider value={value}>{props.children}</AuthContext.Provider>
  );
}

export function useAuthContext() {
  const authContext = useContext(AuthContext);

  assert(
    authContext !== null,
    "useAuthContext must be used within AuthContextProvider",
  );

  return authContext;
}

export function useAuthStore() {
  const value = useAuthContext();
  return value.store;
}
